// Mock data for Shantanu Das Sharma - Leadership Architect website

export const heroData = {
  headline: "Leadership does not fail because of strategy.",
  headlineContinued: "It fails because perception, presence, and patterns remain unconscious.",
  subheading: "Leadership is an inner operating system—one that can be observed, redesigned, and stabilised under pressure.",
  selectivityNote: "This work is for leaders who refuse to lead unconsciously.",
  primaryCta: "Request a Private Conversation",
  ctaSubtext: "Eligibility based. Confidential."
};

export const problemData = {
  sectionTitle: "The Inner Architecture of Leadership",
  observations: [
    "Decision fatigue that compounds despite experience",
    "Inner noise that persists beneath outer success",
    "Organisations silently mirroring the leader's unexamined inner state",
    "Scale increasing complexity, not clarity"
  ],
  keyInsight: "Most leadership breakdowns are not strategic failures. They are architectural mismatches between inner state and outer demand."
};

export const differentiationData = {
  sectionTitle: "Leadership as Inner Systems Engineering",
  intro: "This is not generic coaching. This is not therapy. This is not motivational intervention.",
  explanation: "Most coaching operates at the level of behaviour, mindset, or performance. This work operates at the level of perception architecture and presence design.",
  concepts: [
    {
      term: "Inner Operating System",
      description: "The foundational patterns that govern how you perceive, decide, and respond"
    },
    {
      term: "Pattern Mind",
      description: "The recurring cognitive structures that shape every leadership moment"
    },
    {
      term: "Thought Code",
      description: "The underlying algorithms that determine your default leadership responses"
    },
    {
      term: "Presence Architecture",
      description: "The structural design of how you occupy space and influence fields"
    }
  ],
  closingNote: "Observable. Repeatable. Redesignable."
};

export const authorityData = {
  name: "Shantanu Das Sharma",
  title: "Leadership Architect",
  credentials: [
    "30+ years in management and leadership",
    "10+ years in training and coaching",
    "Worked with CEOs, Founders, and CXOs across India",
    "Deep integration of neuroscience, systems thinking, NLP, and yogic sciences"
  ],
  philosophy: "He does not teach leaders what to do. He works on how leaders perceive, decide, recover, and evolve.",
  quoteAuthor: "Sue Knight",
  quoteAuthorTitle: "Leading Business Consultant, Speaker and Author"
};

export const transformationData = {
  sectionTitle: "The Shift",
  before: {
    title: "Before",
    states: [
      "Reactive leadership",
      "Cognitive overload",
      "Emotional leakage into decisions",
      "Leadership performed from effort"
    ]
  },
  after: {
    title: "After",
    states: [
      "Calm authority",
      "Emotional composure",
      "Coherent decision-making under pressure",
      "Leadership emerging from clarity, not control"
    ]
  },
  promise: "Stability, clarity, and integrity under complexity."
};

export const processData = {
  sectionTitle: "The Work",
  intro: "This is ongoing architectural work, not episodic coaching.",
  steps: [
    {
      title: "Diagnosis",
      description: "Mapping the inner architecture—perception patterns, presence defaults, decision structures"
    },
    {
      title: "Redesign",
      description: "Reconfiguring perception and presence at the structural level"
    },
    {
      title: "Practice Hygiene",
      description: "Establishing sustainable routines, not peak experiences"
    },
    {
      title: "Long-term Coherence",
      description: "Building enduring stability over short-term interventions"
    }
  ]
};

export const faqData = {
  sectionTitle: "Frequently Asked Questions",
  questions: [
    {
      question: "Who is this work designed for?",
      answer: "This work is designed for Founder CEOs, Corporate CXOs, and senior leaders who already carry significant responsibility and seek clarity, not motivation. It is for those who recognise that their inner state directly shapes their organisation's reality."
    },
    {
      question: "How is this different from executive coaching?",
      answer: "Executive coaching typically focuses on behaviour modification, goal achievement, or performance enhancement. This work operates at a deeper level—the architecture of perception, presence, and pattern. It addresses the root system, not the symptoms."
    },
    {
      question: "What does a typical engagement look like?",
      answer: "Engagements are designed around the individual leader's architecture and context. There is no standardised program. Duration and structure emerge from the diagnostic phase and evolve based on the work itself."
    },
    {
      question: "Is this confidential?",
      answer: "Absolute confidentiality is foundational. No details, observations, or insights from our work are ever shared. This is a private, protected space for inner architectural work."
    },
    {
      question: "How do I know if I am ready for this work?",
      answer: "Readiness manifests as a quiet recognition that something needs to shift at the structural level—not more strategies, not more effort, but a fundamental reconfiguration of how you lead. If you are seeking quick fixes, this is not the right fit."
    },
    {
      question: "What is the initial conversation like?",
      answer: "The initial conversation is an exploratory dialogue to assess mutual fit. It is not a sales call. It is a space to understand your current architecture, your aspirations, and whether this work is appropriate for your context."
    }
  ]
};

export const ctaData = {
  headline: "Request a Private Conversation",
  description: "This is not a program for everyone. Eligibility and fit are assessed before any engagement.",
  primaryCta: "Request a Private Conversation",
  secondaryCta: "Read the Author's Note"
};

export const navigationData = {
  logo: "Shantanu Das Sharma",
  links: [
    { label: "About", href: "#about" },
    { label: "The Work", href: "#work" },
    { label: "Process", href: "#process" },
    { label: "Questions", href: "#faq" }
  ],
  cta: "Connect"
};

export const footerData = {
  name: "Shantanu Das Sharma",
  tagline: "Leadership Architect",
  copyright: `© ${new Date().getFullYear()} Shantanu Das Sharma. All rights reserved.`,
  note: "For leaders who refuse to lead unconsciously."
};
